import React, { forwardRef, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Card } from 'antd'
import SystemMessage from './SystemMessage'
import CategoryChoiceMessage from './CategoryChoiceMessage'
import DiffReviewMessage from './DiffReviewMessage'
import ThinkingMessage from './ThinkingMessage'
import WelcomeMessage from './WelcomeMessage'
import { backgroundColor } from '../../../../../../../constants/styles'
import { RefinementItemTypes } from '../constants'
import { WELCOME_MESSAGE, SYSTEM_MESSAGE, THINKING_MESSAGE } from '../messageUtils'

/**
 * A component that renders different types of messages in the chat interface
 * It handles both UI-specific messages and RefinementItems
 */
const ChatMessage = forwardRef(({ message, isLastMessage }, ref) => {
    const messageRef = useRef(null)

    useEffect(() => {
        if (isLastMessage && messageRef.current) {
            setTimeout(() => {
                messageRef.current?.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'nearest'
                })
            }, 100)
        }
    }, [isLastMessage, message])

    // Determine which component to render based on message type
    let MessageComponent = null;
    
    // Handle UI-specific message types
    if (message.type === WELCOME_MESSAGE) {
        MessageComponent = WelcomeMessage;
    } else if (message.type === SYSTEM_MESSAGE) {
        MessageComponent = SystemMessage;
    } else if (message.type === THINKING_MESSAGE) {
        MessageComponent = ThinkingMessage;
    } 
    // Handle RefinementItem types
    else if (message.type === RefinementItemTypes.SUBMISSION) {
        MessageComponent = CategoryChoiceMessage;
    } else if (message.type === RefinementItemTypes.SUGGESTION) {
        MessageComponent = DiffReviewMessage;
    }
    
    if (!MessageComponent) return null;
    
    return (
        <Card size='small' style={{ backgroundColor }} ref={messageRef}>
            {/* For UI-specific messages */}
            {(message.type === WELCOME_MESSAGE || message.type === SYSTEM_MESSAGE) && (
                <MessageComponent content={message.content} />
            )}
            
            {/* For thinking messages */}
            {message.type === THINKING_MESSAGE && (
                <MessageComponent content={{ state: message.state }} />
            )}
            
            {/* For RefinementItems */}
            {(message.type === RefinementItemTypes.SUBMISSION || message.type === RefinementItemTypes.SUGGESTION) && (
                <MessageComponent refinementItem={message} />
            )}
        </Card>
    )
})

ChatMessage.propTypes = {
    message: PropTypes.oneOfType([
        PropTypes.shape({
            type: PropTypes.string.isRequired,
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            content: PropTypes.any,
            state: PropTypes.string
        }),
        PropTypes.shape({
            type: PropTypes.oneOf([RefinementItemTypes.SUBMISSION, RefinementItemTypes.SUGGESTION]).isRequired,
            id: PropTypes.number.isRequired
        })
    ]).isRequired,
    isLastMessage: PropTypes.bool,
}

export default ChatMessage
